
import React, { useState, useEffect, useRef } from 'react';

/**
 * Hook para persistir estados de formulário (rascunhos) no sessionStorage.
 * Sobrevive a trocas de abas do app, reloads e trocas de abas do navegador.
 */
// Added React import and comment to resolve namespace error on line 8.
export function useFormDraft<T>(key: string, initialState: T): [T, React.Dispatch<React.SetStateAction<T>>, () => void, boolean] {
  const [state, setState] = useState<T>(() => {
    if (typeof window === 'undefined') return initialState;
    
    try {
      const saved = sessionStorage.getItem(`draft:${key}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge para garantir que novos campos adicionados no código existam mesmo em rascunhos antigos
        return { ...initialState, ...parsed };
      }
    } catch (e) {
      console.error(`[useFormDraft] Erro ao carregar rascunho ${key}:`, e);
    }
    return initialState;
  });

  const [hasDraft, setHasDraft] = useState(false);
  const isFirstMount = useRef(true);

  // Verifica se há rascunho salvo no carregamento
  useEffect(() => {
    const saved = sessionStorage.getItem(`draft:${key}`);
    setHasDraft(!!saved);
  }, [key]);

  // Salvar rascunho com debounce (300ms)
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    const timeout = setTimeout(() => {
      try {
        // Apenas salva se for diferente do estado inicial para economizar processamento
        if (JSON.stringify(state) !== JSON.stringify(initialState)) {
          sessionStorage.setItem(`draft:${key}`, JSON.stringify(state));
          setHasDraft(true);
        }
      } catch (e) {
        console.error(`[useFormDraft] Erro ao salvar rascunho ${key}:`, e);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [state, key, initialState]);

  const clearDraft = () => {
    sessionStorage.removeItem(`draft:${key}`);
    setState(initialState);
    setHasDraft(false);
  };

  return [state, setState, clearDraft, hasDraft];
}
